/**
 * Quien esta usando TORA, en un solo lugar.
 *
 * TRES ESTADOS, NO DOS
 * --------------------
 * Casi toda la app de este tipo modela "hay sesion" / "no hay sesion" y se
 * equivoca en el tercer caso, que es el primero que ocurre en la vida del
 * sistema: la base todavia no tiene NINGUN usuario. Mandar a esa persona al
 * login es mandarla a una pared sin puerta — no existe credencial que pueda
 * funcionar, y el unico camino es `POST /sesion/instalar`, que se cierra para
 * siempre en cuanto hay un usuario.
 *
 * Por eso `RequiereSesion` decide entre tres destinos:
 *
 *   cargando            -> no se mueve. Redirigir antes de saber quien sos es
 *                          como te vas al login estando logueado.
 *   sin instalar        -> `/instalar`
 *   instalado sin login -> `/login`
 *
 * EL 401 QUE LLEGA SOLO
 * ---------------------
 * La cookie dura 24 horas y vence sola. El caso real es la maquina del mostrador
 * que queda abierta toda la noche: a la mañana la pantalla sigue mostrando el
 * saldo de un cliente y el primer click contesta 401. `alPerderSesion()` de
 * `api.ts` avisa en ese instante y aca se limpia el estado, para que la app no
 * se quede con datos de ayer y errores en silencio.
 */
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { alPerderSesion } from "@/lib/api";
import {
  claves,
  useEstadoInstalacion,
  useInstalar,
  useLogin,
  useLogout,
  useSesion,
} from "@/lib/queries";
import type { CredencialesLogin, DatosInstalacion, Usuario } from "@/lib/tipos";

export interface Auth {
  /** `null` es "no hay nadie logueado". No es un error. */
  usuario: Usuario | null;
  /** Todavia no se sabe ni quien sos ni si el sistema esta instalado. */
  cargando: boolean;
  /**
   * Si la base ya tiene al menos un usuario.
   *
   * Ante la duda vale `true`. Si la consulta de estado falla (backend caido,
   * red), asumir "sin instalar" mandaria al dueño a crear una cuenta que ya
   * existe, y el backend contesta 409: un callejon. Asumir "instalado" lo manda
   * al login, que como maximo falla con un mensaje que se entiende.
   */
  instalado: boolean;
  /** Atajo de `usuario !== null`, que es la pregunta que se hace todo el tiempo. */
  autenticado: boolean;
  /** `true` mientras admin y operador se distinguen. */
  esAdmin: boolean;
  /** Hay un login, un logout o una instalacion en vuelo. */
  operando: boolean;
  /** Tira `ErrorApi` con el mensaje del backend: la pantalla lo muestra tal cual. */
  login: (credenciales: CredencialesLogin) => Promise<Usuario>;
  logout: () => Promise<void>;
  /** Alta del primer usuario. Deja la sesion abierta, sin pedir login de nuevo. */
  instalar: (datos: DatosInstalacion) => Promise<Usuario>;
}

const ContextoAuth = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const qc = useQueryClient();
  const consultaSesion = useSesion();
  const consultaInstalacion = useEstadoInstalacion();
  const mutLogin = useLogin();
  const mutLogout = useLogout();
  const mutInstalar = useInstalar();

  const usuario = consultaSesion.data ?? null;

  // Un 401 desde cualquier consulta baja la sesion. No se toca nada mas aca: el
  // resto de la cache se limpia en el efecto de abajo, cuando la UI ya se
  // desmonto, para no disparar refetchs que vuelvan a chocar contra el 401.
  useEffect(() => alPerderSesion(() => qc.setQueryData(claves.sesion, null)), [qc]);

  // Al pasar de "habia alguien" a "no hay nadie" se borran los datos del
  // usuario anterior. Sin esto, el proximo que entra ve por un instante el saldo
  // y las ventas del que estaba antes, mientras llega el refetch.
  const habiaUsuario = useRef(false);
  useEffect(() => {
    if (usuario) {
      habiaUsuario.current = true;
      return;
    }
    if (!habiaUsuario.current) return;
    habiaUsuario.current = false;
    qc.removeQueries({ predicate: (query) => query.queryKey[0] !== "sesion" });
  }, [usuario, qc]);

  const login = useCallback(
    async (credenciales: CredencialesLogin) => {
      const r = await mutLogin.mutateAsync(credenciales);
      return r.usuario;
    },
    [mutLogin],
  );

  const logout = useCallback(async () => {
    await mutLogout.mutateAsync();
  }, [mutLogout]);

  const instalar = useCallback(
    async (datos: DatosInstalacion) => {
      const r = await mutInstalar.mutateAsync(datos);
      return r.usuario;
    },
    [mutInstalar],
  );

  const valor = useMemo<Auth>(
    () => ({
      usuario,
      cargando: consultaSesion.isPending || consultaInstalacion.isPending,
      instalado: consultaInstalacion.data?.instalado ?? true,
      autenticado: usuario !== null,
      esAdmin: usuario?.rol === "admin",
      operando: mutLogin.isPending || mutLogout.isPending || mutInstalar.isPending,
      login,
      logout,
      instalar,
    }),
    [
      usuario,
      consultaSesion.isPending,
      consultaInstalacion.isPending,
      consultaInstalacion.data,
      mutLogin.isPending,
      mutLogout.isPending,
      mutInstalar.isPending,
      login,
      logout,
      instalar,
    ],
  );

  return <ContextoAuth.Provider value={valor}>{children}</ContextoAuth.Provider>;
}

/** El estado de sesion. Fuera del provider es un error de armado, no un `null`. */
export function useAuth(): Auth {
  const ctx = useContext(ContextoAuth);
  if (!ctx) throw new Error("useAuth se usa adentro de <AuthProvider>.");
  return ctx;
}

// ─────────────────────────────────────────────────────────────────────────────
// El guardia
// ─────────────────────────────────────────────────────────────────────────────

export type Destino = "/instalar" | "/login" | null;

/**
 * Las pantallas que se ven SIN sesion.
 *
 * El login y la instalacion estan aca y no en cada pantalla porque son la
 * condicion de borde del guardia: sin esta lista, poner `RequiereSesion`
 * alrededor de toda la app manda al login a alguien que ya esta en el login, y
 * de ahi al login otra vez.
 *
 * `/catalogo` es distinto: no es una pantalla del sistema, es LA pantalla del
 * cliente. Se abre desde el link del Linktree, desde un telefono cualquiera.
 * Hasta que entro en esta lista, ese link mandaba al login, que es lo mismo que
 * no tener catalogo. Lo que sirve ahi sale de vistas que no traen costos ni
 * margenes (migracion 0009), asi que no hay nada que proteger con una sesion.
 */
export const RUTAS_PUBLICAS: readonly string[] = ["/login", "/instalar", "/catalogo"];

/**
 * Si la ruta que el router tiene MONTADA es publica. No alcanza con la
 * direccion: mientras se redirige de «/» a /login la direccion ya dice /login
 * pero la ruta montada sigue siendo el Mostrador, y dibujarla fuera del panel
 * tiraba «useBuscador tiene que usarse adentro de <BuscadorProvider>».
 */
export function rutaMontadaEsPublica(
  matches: readonly { pathname: string }[],
  direccion: string,
): boolean {
  return RUTAS_PUBLICAS.includes(matches[matches.length - 1]?.pathname ?? direccion);
}

/**
 * A donde hay que mandar a esta persona, o `null` si se queda donde esta.
 *
 * Se exporta suelta y sin React adentro porque es la regla que importa y se
 * puede probar exhaustivamente sin montar un router.
 */
export function destinoSegunSesion(estado: {
  cargando: boolean;
  instalado: boolean;
  autenticado: boolean;
}): Destino {
  // Mientras no se sabe, no se redirige: mandar al login a alguien que tiene
  // sesion valida es el bug clasico de esta pantalla.
  if (estado.cargando) return null;
  if (!estado.instalado) return "/instalar";
  if (!estado.autenticado) return "/login";
  return null;
}

/**
 * Envuelve lo que solo puede ver alguien con sesion.
 *
 * Mientras se resuelve muestra `alCargar` (por defecto, nada: un spinner que
 * aparece y desaparece en 80 ms es peor que un momento de pantalla quieta).
 */
export function RequiereSesion({
  children,
  alCargar = null,
  publica: enRutaPublica,
}: {
  children: ReactNode;
  alCargar?: ReactNode;
  /**
   * Lo que se dibuja en una ruta publica EN LUGAR de `children`. Sin esto, el
   * login y el catalogo montaban el panel entero por detras —la barra de
   * estado, el buscador— y cada uno pedia datos protegidos que volvian 401:
   * veinte errores en la consola antes de que nadie iniciara sesion.
   */
  publica?: ReactNode;
}): JSX.Element | null {
  const { cargando, instalado, autenticado } = useAuth();
  const navegar = useNavigate();
  const ruta = useRouterState({ select: (s) => s.location.pathname });
  const publica = RUTAS_PUBLICAS.includes(ruta);

  const destino = publica ? null : destinoSegunSesion({ cargando, instalado, autenticado });

  useEffect(() => {
    if (destino === null) return;
    // `replace`: el back del navegador tiene que volver a donde la persona
    // estaba, no rebotar contra el login una y otra vez.
    void navegar({ to: destino, replace: true });
  }, [destino, navegar]);

  // El login y la instalacion se ven siempre: son las pantallas de las que
  // depende que exista una sesion.
  if (publica) return <>{enRutaPublica ?? children}</>;
  if (cargando) return <>{alCargar}</>;
  // Ya se esta redirigiendo: renderizar el contenido protegido aunque sea un
  // frame significa pedir datos que van a contestar 401.
  if (destino !== null) return null;
  return <>{children}</>;
}
